/*
 * Exercise B question generator (FR-EXB-01).
 *
 * Responsibilities (this module only):
 *   - Delegate source-content selection to FR-EXB-02's
 *     `pickExerciseBContent` (word, runtime-built combo, short_line, or
 *     long_line depending on level + review flag).
 *   - Choose one rune position within the source to blank out, subject
 *     to two constraints:
 *       1. The blanked glyph must correspond to a rune the user has
 *          been exposed to (level ≤ N) — i.e. it appears in the
 *          canonical rune set defined below.
 *       2. For current-level questions (`isReview === false`), prefer
 *          positions whose rune was introduced at the current level
 *          when at least one such position exists. Otherwise pick
 *          uniformly from all eligible positions. This implements the
 *          "current-level bias" arm of the FR-LOGIC-01 75/25 mix at
 *          the question-construction layer; the session runner picks
 *          the 75/25 split, this generator honours it position-wise.
 *   - Assemble multiple-choice options: target rune + up to 3
 *     distractors via FR-LOGIC-03's `pickDistractors`, then Fisher-
 *     Yates shuffle. The "minimum 2 total options" floor is enforced
 *     implicitly the same way Exercise A does it — if the eligible
 *     pool yields no distractors we still ship a 1-option question;
 *     the content-layer guarantee (L1 always exposes ≥5 runes) keeps
 *     that branch unreachable in practice.
 *
 * Canonical rune set: the deduped set of single-glyph `type='word'`
 * entries at level ≤ N. Each level's five word rows are the per-level
 * rune introductions (CLAUDE.md), and a "rune" by definition is one
 * glyph — multi-glyph word entries (combos built at runtime, or any
 * future multi-rune CSV `word` rows) are NOT canonical rune entries.
 * This filter mirrors the pool interpretation used by FR-LOGIC-03 in
 * `src/data/distractors.ts`.
 *
 * Scope boundary: this generator builds *one* question for *one*
 * caller-supplied level + review flag. Session composition
 * (FR-LOGIC-02's "every current-level rune appears at least once per
 * session", FR-LOGIC-01's 75/25 orchestration) is the session
 * runner's job (#22/#23), not this module's.
 *
 * Pure function: no module-level state, no `getCorpus()` calls. The
 * corpus, current level, and review flag all come from the caller.
 * RNG is injected via `options.random` and forwarded to BOTH
 * `pickExerciseBContent` AND `pickDistractors` so a single seeded
 * RNG drives the entire pipeline — that keeps the deterministic tests
 * below short.
 */

import type { RuneEntry } from './csv';
import { pickExerciseBContent } from './exercise-b-content';
import { pickDistractors } from './distractors';

export interface ExerciseBQuestion {
  /** The source content from FR-EXB-02 — word, combo, short_line, or long_line. */
  source: RuneEntry;
  /** Index into `Array.from(source.rune)` of the blanked-out position. */
  blankIndex: number;
  /** The correct rune entry (the one at `blankIndex` in the source). */
  target: RuneEntry;
  /** Options as displayed, including the target. Length 2..totalOptions. */
  options: RuneEntry[];
  /** Position of `target` within `options` after the shuffle. */
  correctIndex: number;
}

export interface GenerateExerciseBOptions {
  /**
   * Total max options including the target. Must be an integer in
   * [2, 4]. Out-of-range or non-integer values fall back silently to
   * the default. Default 4.
   */
  totalOptions?: number;
  /** Injectable RNG returning a float in [0, 1). Default Math.random. */
  random?: () => number;
  /**
   * When true, the question is part of the 25% review mix
   * (FR-LOGIC-01) — the blank position is picked uniformly from ALL
   * eligible positions with no current-level preference, and the flag
   * is forwarded to `pickExerciseBContent` so source selection spans
   * easier difficulties as well. Default false.
   */
  isReview?: boolean;
}

const DEFAULT_TOTAL_OPTIONS = 4;
const MIN_TOTAL_OPTIONS = 2;

/**
 * Coerce a caller-supplied `totalOptions` into the allowed range.
 * Mirrors `sanitizeTotalOptions` in `src/data/exercise-a.ts` — we
 * silently fall back rather than throw because the only legitimate
 * callers are internal and the floor/ceiling match the AC's minimum-
 * options rule and the design's tile count respectively.
 */
function sanitizeTotalOptions(raw: number | undefined): number {
  if (raw === undefined) return DEFAULT_TOTAL_OPTIONS;
  if (!Number.isInteger(raw)) return DEFAULT_TOTAL_OPTIONS;
  if (raw < MIN_TOTAL_OPTIONS || raw > DEFAULT_TOTAL_OPTIONS) return DEFAULT_TOTAL_OPTIONS;
  return raw;
}

/**
 * Build one Exercise B question for the given level.
 *
 * Pipeline:
 *   1. Sanitize `totalOptions` and resolve the RNG.
 *   2. Delegate source selection to `pickExerciseBContent`, forwarding
 *      the same RNG + `isReview` flag.
 *   3. Build the canonical rune lookup (single-glyph type='word'
 *      entries at level ≤ currentLevel, deduped by glyph).
 *   4. Find eligible blank positions inside `source.rune` — positions
 *      whose glyph appears in the lookup table.
 *   5. Apply the current-level preference when `isReview === false`
 *      AND at least one eligible position is itself a current-level
 *      rune; otherwise use all eligible positions.
 *   6. Pick one position with the injected RNG, look up the target
 *      entry, assemble + shuffle options via `pickDistractors`.
 *
 * Returns null when:
 *   - `pickExerciseBContent` returns null (corpus has no usable
 *     content at any difficulty for this level), OR
 *   - the source content has zero positions whose glyph is in the
 *     canonical rune set (shouldn't happen with healthy content but
 *     graceful — e.g. a synthetic test corpus where the source
 *     entry's glyphs don't overlap the word-row glyphs).
 *
 * Pure function: same inputs + same RNG produce the same output.
 */
export function generateExerciseB(
  corpus: readonly RuneEntry[],
  level: number,
  options?: GenerateExerciseBOptions,
): ExerciseBQuestion | null {
  const totalOptions = sanitizeTotalOptions(options?.totalOptions);
  const random = options?.random ?? Math.random;
  const isReview = options?.isReview ?? false;

  // Step 1: source content. The picker handles the difficulty ladder,
  // fallback chain, and L3–4 combo construction — we just consume its
  // output. Forwarding the same RNG means a single seed drives the
  // entire question (source + position + distractors + shuffle).
  const source = pickExerciseBContent(corpus, level, { isReview, random });
  if (source === null) return null;

  // Step 2: canonical rune lookup. Keyed by the single-glyph `rune`
  // field of a deduped `type='word'` entry at level ≤ currentLevel.
  // Multi-glyph word entries (runtime combos, future multi-rune word
  // rows) are NOT canonical and are skipped — they don't introduce a
  // new rune, they reuse existing ones.
  const runeByGlyph = buildRuneLookup(corpus, level);

  // Step 3: eligible blank positions. `Array.from` decomposes the
  // source's rune string into Unicode code points so supplementary-
  // plane glyphs survive (the bespoke rune font ships in the SMP).
  // Elder Futhark (U+16A0–16FF) is in the BMP so `.split('')` would
  // work today, but `Array.from` is the idiom used elsewhere in the
  // codebase (see `src/screens/level-intro.ts`) and is forward-safe.
  const glyphs = Array.from(source.rune);
  const eligible: Array<{ index: number; entry: RuneEntry }> = [];
  for (let i = 0; i < glyphs.length; i++) {
    const glyph = glyphs[i]!;
    const entry = runeByGlyph.get(glyph);
    if (entry !== undefined) {
      eligible.push({ index: i, entry });
    }
  }
  if (eligible.length === 0) return null;

  // Step 4: current-level preference. When this is a current-level
  // question (75% arm of the mix) AND at least one eligible position
  // is itself a current-level rune, restrict to those. Otherwise
  // (review arm, OR no current-level glyphs in the source) keep the
  // full eligible pool. This narrows the random pick without changing
  // its uniformity within the narrowed pool.
  let pool: Array<{ index: number; entry: RuneEntry }> = eligible;
  if (!isReview) {
    const currentLevelHits = eligible.filter((slot) => slot.entry.level === level);
    if (currentLevelHits.length > 0) {
      pool = currentLevelHits;
    }
  }

  // Step 5: pick one position uniformly from the (possibly narrowed)
  // pool with the injected RNG.
  const pickIdx = Math.floor(random() * pool.length);
  const chosen = pool[pickIdx]!;
  const blankIndex = chosen.index;
  const target = chosen.entry;

  // Step 6: assemble options. Forward the sanitized totalOptions and
  // the same injected RNG so the partial Fisher-Yates inside
  // `pickDistractors` is seeded predictably.
  const distractors = pickDistractors(target, corpus, level, {
    totalOptions,
    random,
  });

  const combined: RuneEntry[] = [target, ...distractors];
  shuffleInPlace(combined, random);

  // Reference equality is safe: `runeByGlyph` stores the exact entry
  // objects from the corpus and `pickDistractors` never returns the
  // target (it excludes by `latin`). The target identity therefore
  // survives the spread + shuffle and `indexOf` finds it.
  const correctIndex = combined.indexOf(target);

  return { source, blankIndex, target, options: combined, correctIndex };
}

/**
 * Build the deduped canonical rune set introduced at levels 1..N.
 * Returns a Map keyed by the rune glyph so position-by-position
 * lookup against the source content is O(1) per glyph.
 *
 * Filter rules (single-glyph word entry):
 *   - `type === 'word'` (line entries are reading content, not rune
 *     introductions).
 *   - `level <= currentLevel` (gating: never expose a rune the user
 *     hasn't unlocked yet).
 *   - `Array.from(entry.rune).length === 1` (a "rune" is one glyph;
 *     multi-glyph word entries reuse existing runes and are not new
 *     introductions).
 *   - First-occurrence wins on duplicate glyphs — the corpus order
 *     reflects intended introduction order, and the canonical
 *     interpretation is "first appearance defines the rune entry".
 */
function buildRuneLookup(
  corpus: readonly RuneEntry[],
  currentLevel: number,
): Map<string, RuneEntry> {
  const runeByGlyph = new Map<string, RuneEntry>();
  for (const entry of corpus) {
    if (entry.type !== 'word') continue;
    if (entry.level > currentLevel) continue;
    if (Array.from(entry.rune).length !== 1) continue;
    if (runeByGlyph.has(entry.rune)) continue;
    runeByGlyph.set(entry.rune, entry);
  }
  return runeByGlyph;
}

/**
 * Standard Fisher-Yates shuffle, in place. Iterates from the last
 * index down to 1, swapping each element with a uniformly random
 * earlier-or-equal slot. Identical implementation to Exercise A's
 * shuffle (intentionally not factored to a shared helper — keeping
 * the generators self-contained matches the codebase convention).
 */
function shuffleInPlace<T>(array: T[], random: () => number): void {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    const tmp = array[i]!;
    array[i] = array[j]!;
    array[j] = tmp;
  }
}
